import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from "@illustraclass/ui";
import { Mountain, Shield, Dna, LogOut } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { api } from "../lib/api";

type Experiment = {
  identifier: string;
  url: string;
  name: string;
  description: string;
  category: string;
  status: string;
  icon: string;
};

type ExperimentsResponse = {
  itemListElement: Experiment[];
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  mountain: Mountain,
  shield: Shield,
  dna: Dna,
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [isLoadingExperiments, setIsLoadingExperiments] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchExperiments = useCallback(async () => {
    setIsLoadingExperiments(true);
    setFetchError(null);
    try {
      const data = await api<ExperimentsResponse>("/api/experiments");
      setExperiments(data.itemListElement);
    } catch {
      setFetchError(
        "Impossible de charger les expériences. Vérifiez votre connexion.",
      );
    } finally {
      setIsLoadingExperiments(false);
    }
  }, []);

  useEffect(() => {
    void fetchExperiments();
  }, [fetchExperiments]);

  async function handleLogout() {
    await logout();
    void navigate("/login");
  }

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">IllustraClass</h1>
            <p className="text-sm text-muted-foreground">
              Bienvenue, {user?.displayName}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => {
                  void navigate("/admin/teachers");
                }}
              >
                Administration
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                void handleLogout();
              }}
              aria-label="Se déconnecter"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl p-6">
        <h2 className="mb-6 text-2xl font-bold text-foreground">
          Expériences disponibles
        </h2>

        {fetchError ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <p role="alert" className="text-sm text-destructive">
              {fetchError}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                void fetchExperiments();
              }}
            >
              Réessayer
            </Button>
          </div>
        ) : isLoadingExperiments ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : experiments.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-lg text-muted-foreground">
              Aucune expérience disponible pour le moment.
            </p>
            <p className="text-sm text-muted-foreground">
              De nouvelles expériences seront ajoutées prochainement.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {experiments.map((exp) => {
              const IconComponent = ICON_MAP[exp.icon] ?? Mountain;
              const isActive = exp.status === "active";

              return (
                <Card key={exp.identifier} className="flex flex-col">
                  <CardHeader>
                    <div className="mb-2 flex items-center gap-2">
                      <IconComponent className="h-6 w-6 text-primary" />
                      <Badge variant={isActive ? "default" : "secondary"}>
                        {isActive ? "Disponible" : "Bientôt"}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{exp.name}</CardTitle>
                    <CardDescription>{exp.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <Button
                      className="w-full"
                      size="lg"
                      disabled={!isActive}
                      onClick={() => {
                        void navigate(exp.url);
                      }}
                    >
                      {isActive ? "Lancer au tableau" : "Prochainement"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
